import { ArrowDown, ArrowRight, ArrowUp } from 'lucide-react';
import { FORMULA_OPTIONS, VALUE_TYPE_UNITS } from '../../constants/kpi.constants';
import { Direction, FormulaName, MetricValueResponseDto, RangeValueType, Status } from '../../types/response';

export interface RangeFieldState {
  type: RangeValueType;
  value: string;
  formula: FormulaName | null;
}

export interface RangeEditState {
  min: RangeFieldState;
  max: RangeFieldState;
}

export const METRIC_ROW_MIN_WIDTH_PX = 230;

interface MetricRowProps {
  metric: MetricValueResponseDto;
  isEditMode: boolean;
  editValue: string;
  onValueChange: (value: string) => void;
  showMinMax?: boolean;
  rangeState?: RangeEditState;
  onRangeChange?: (next: RangeEditState) => void;
}

const STATUS_BADGE_STYLES: Record<Status, string> = {
  [Status.POSITIVE]: 'bg-[#C0DE60]/15 text-[#C0DE60] border border-[#C0DE60]',
  [Status.NEGATIVE]: 'bg-[#CE0909]/20 text-[#CE0909] border border-[#CE0909]',
  [Status.NEUTRAL]: 'bg-[#FFFFFF] text-[#12282A] border border-[#D0D5DD]'
};

const DELTA_STYLES: Record<Direction, string> = {
  [Direction.UP]: 'text-[#C0DE60]',
  [Direction.DOWN]: 'text-[#CE0909]',
  [Direction.NEUTRAL]: 'text-[#D0D5DD]'
};

const DELTA_INVERTED_STYLES: Record<Direction, string> = {
  [Direction.UP]: 'text-[#CE0909]',
  [Direction.DOWN]: 'text-[#C0DE60]',
  [Direction.NEUTRAL]: 'text-[#D0D5DD]'
};

function formatValue(value: number | null, valueType: MetricValueResponseDto['valueType']): string {
  if (value === null) return '–';

  const unit = VALUE_TYPE_UNITS[valueType];

  switch (valueType) {
    case 'currency':
      return `${value.toLocaleString('de-DE')}${unit}`;
    case 'percentage':
      return `${value}${unit}`;
    case 'decimal':
      return value.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
    case 'integer':
    default:
      return value.toLocaleString('de-DE');
  }
}

function DeltaIndicator({
  direction,
  percentage,
  isDeltaInverted,
  alwaysNeutralColor
}: {
  direction: Direction | null;
  percentage: number | null;
  isDeltaInverted: boolean;
  alwaysNeutralColor: boolean;
}) {
  const realDirection = direction ?? Direction.NEUTRAL;

  const Icon = realDirection === Direction.UP ? ArrowUp : realDirection === Direction.DOWN ? ArrowDown : ArrowRight;
  const style = alwaysNeutralColor
    ? DELTA_STYLES[Direction.NEUTRAL]
    : isDeltaInverted
    ? DELTA_INVERTED_STYLES[realDirection]
    : DELTA_STYLES[realDirection];

  return (
    <div className={`flex items-center gap-0.5 text-[10px] w-[50px] text-center justify-end font-bold ${style}`}>
      <Icon size={12} />
      {percentage ? Math.abs(percentage) : '–'}%
    </div>
  );
}

function RangeInput({ field, onChange, unit }: { field: RangeFieldState; onChange: (next: RangeFieldState) => void; unit: string | null }) {
  const isFormula = field.type === RangeValueType.CALCULATED;
  const defaultFormula = FORMULA_OPTIONS[0]?.value ?? null;

  const toggleType = () => {
    if (isFormula) {
      onChange({ ...field, type: RangeValueType.STATIC, formula: null });
    } else {
      onChange({ ...field, type: RangeValueType.CALCULATED, formula: field.formula ?? defaultFormula });
    }
  };

  const selectedLabel = FORMULA_OPTIONS.find((o) => o.value === (field.formula ?? defaultFormula))?.label ?? '';

  if (isFormula) {
    return (
      <div
        className="inline-flex items-center rounded-full border border-[#D0D5DD] bg-white h-[24px] w-[72px] overflow-hidden group relative"
        title={selectedLabel}>
        <button
          type="button"
          onClick={toggleType}
          className="relative z-10 flex-shrink-0 flex items-center justify-center h-full px-1.5 bg-[#12282A] text-white text-[9px] font-bold rounded-l-full hover:bg-[#1e3c3f] transition-colors"
          title="Zu Wert wechseln">
          =
        </button>
        <select
          value={field.formula ?? defaultFormula ?? ''}
          onChange={(e) => onChange({ ...field, formula: e.target.value as FormulaName })}
          className="absolute top-0 right-0 bottom-0 left-[20px] opacity-0 cursor-pointer">
          {FORMULA_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <span className="truncate pl-1 pr-1.5 text-[10px] font-bold text-[#12282A] pointer-events-none">{selectedLabel}</span>
      </div>
    );
  }

  return (
    <div className="inline-flex gap-1 items-center rounded-full border border-[#D0D5DD] h-[24px] w-[72px] overflow-hidden focus-within:ring-1 focus-within:ring-[#5FD5DE] focus-within:border-[#5FD5DE]">
      <input
        type="text"
        inputMode="decimal"
        value={field.value}
        onChange={(e) => onChange({ ...field, value: e.target.value })}
        placeholder="–"
        className="min-w-0 flex-1 text-center text-[10px] font-bold bg-transparent px-1 h-full focus:outline-none"
      />
      {unit && <span className="flex-shrink-0 text-[10px] text-[#98A2B3] pointer-events-none">{unit}</span>}
      <button
        type="button"
        onClick={toggleType}
        className="flex-shrink-0 flex items-center justify-center h-full px-1.5 text-[9px] bg-[#D0D5DD] font-bold text-[#98A2B3] hover:text-[#12282A] hover:bg-[#F2F4F7] rounded-r-full transition-colors"
        title="Zu Formel wechseln">
        fx
      </button>
    </div>
  );
}

function MetricRowView({ metric, showMinMax }: { metric: MetricValueResponseDto; showMinMax: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {!showMinMax ? (
        <>
          <div
            className={`flex items-center justify-center rounded-full w-[61px] px-3 py-1 text-[10px] font-bold ${
              STATUS_BADGE_STYLES[metric.status]
            }`}>
            {formatValue(metric.value, metric.valueType)}
          </div>
          <DeltaIndicator
            direction={metric.deltaDirection}
            percentage={metric.deltaPercentage}
            isDeltaInverted={metric.isDeltaInverted}
            alwaysNeutralColor={metric.displayVariant === 'footer'}
          />
        </>
      ) : (
        <div className="flex items-center gap-1.5 justify-end">
          <div className="flex items-center justify-center rounded-full px-3 py-1 w-[61px] text-[10px] font-bold border border-[#D0D5DD] text-[#12282A] bg-white">
            {formatValue(metric.min?.value ?? null, metric.valueType)}
          </div>
          <span className="text-[10px] font-light text-[#D0D5DD]">–</span>
          <div className="flex items-center justify-center rounded-full px-3 py-1 w-[61px] text-[10px] font-bold border border-[#D0D5DD] text-[#12282A] bg-white">
            {formatValue(metric.max?.value ?? null, metric.valueType)}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricRowEdit({
  metric,
  editValue,
  onValueChange,
  showMinMax,
  rangeState,
  onRangeChange
}: {
  metric: MetricValueResponseDto;
  editValue: string;
  onValueChange: (value: string) => void;
  showMinMax: boolean;
  rangeState?: RangeEditState;
  onRangeChange?: (next: RangeEditState) => void;
}) {
  const unit = VALUE_TYPE_UNITS[metric.valueType];

  return (
    <div className="relative flex items-center gap-2">
      {!showMinMax ? (
        <div className="inline-flex items-center rounded-full border border-[#D0D5DD] h-[24px] w-[72px] overflow-hidden focus-within:ring-1 focus-within:ring-[#5FD5DE] focus-within:border-[#5FD5DE]">
          <input
            type="text"
            inputMode="decimal"
            value={editValue}
            onChange={(e) => onValueChange(e.target.value)}
            placeholder="–"
            className="min-w-0 flex-1 text-center text-[10px] font-bold bg-transparent px-1 h-full focus:outline-none"
          />
          {unit && <span className="flex-shrink-0 text-[10px] text-[#98A2B3] pr-1.5 pointer-events-none">{unit}</span>}
        </div>
      ) : rangeState && onRangeChange ? (
        <div className="flex items-center gap-1.5 justify-end">
          <RangeInput field={rangeState.min} onChange={(min) => onRangeChange({ ...rangeState, min })} unit={unit} />
          <span className="text-[10px] font-light text-[#D0D5DD]">–</span>
          <RangeInput field={rangeState.max} onChange={(max) => onRangeChange({ ...rangeState, max })} unit={unit} />
        </div>
      ) : null}
    </div>
  );
}

const MetricRow = ({ metric, isEditMode, editValue, onValueChange, showMinMax = false, rangeState, onRangeChange }: MetricRowProps) => {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[12px] mr-5 font-bold text-[#12282A]">{metric.label}:</span>

      {isEditMode ? (
        <MetricRowEdit
          metric={metric}
          editValue={editValue}
          onValueChange={onValueChange}
          showMinMax={showMinMax}
          rangeState={rangeState}
          onRangeChange={onRangeChange}
        />
      ) : (
        <MetricRowView metric={metric} showMinMax={showMinMax} />
      )}
    </div>
  );
};

export default MetricRow;

export const MetricRowSkeleton = () => (
  <div className="flex items-center py-[3px] w-full">
    <div className="h-3 bg-[#E4E7EC] rounded-full w-full" />
  </div>
);

export const MetricFooterRowSkeleton = () => (
  <div className="flex items-center py-[3px] w-full">
    <div className="h-3 bg-[#E4E7EC] rounded-full w-full" />
  </div>
);
